import { Button } from "@/components/ui/button";
import { ArrowRight, Upload, Zap, Sparkles, Star, ShieldCheck, Clock, BookOpen, Brain, FileText } from "lucide-react";

const Hero = () => {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section className="relative min-h-screen gradient-hero pt-20 sm:pt-24 pb-12 sm:pb-20 overflow-hidden">
      {/* Animated background blobs */}
      <div className="absolute top-10 -left-20 sm:left-0 w-72 sm:w-[28rem] h-72 sm:h-[28rem] bg-primary/20 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-0 -right-20 sm:right-0 w-80 sm:w-[34rem] h-80 sm:h-[34rem] bg-accent/20 rounded-full blur-3xl animate-float animation-delay-300" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 w-64 sm:w-[20rem] h-64 sm:h-[20rem] bg-purple-500/10 rounded-full blur-3xl" />

      {/* Subtle grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-7xl mx-auto">
          {/* Left content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full glass text-primary text-xs sm:text-sm font-semibold mb-4 sm:mb-6 animate-slide-up shadow-soft">
              <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span>India's #1 AI Study Assistant</span>
              <span className="ml-1 px-1.5 sm:px-2 py-0.5 rounded-full bg-accent/20 text-accent text-[9px] sm:text-[10px] font-bold uppercase">New</span>
            </div>

            {/* Heading */}
            <h1 className="text-[2rem] sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-foreground mb-4 sm:mb-6 leading-[1.1] tracking-tight animate-slide-up animation-delay-100">
              Snap Your Notes.
              <span className="text-gradient block mt-1 sm:mt-2">Ace Every Exam.</span>
            </h1>

            {/* Subheading */}
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-5 sm:mb-8 animate-slide-up animation-delay-200 leading-relaxed">
              Turn messy handwritten notes into clean text, assignments, flashcards, quizzes & PPTs in seconds — powered by AI built for students.
            </p>

            {/* Free Credits Badge */}
            <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full bg-green-500/15 text-green-600 dark:text-green-400 text-xs sm:text-sm font-semibold mb-6 sm:mb-8 animate-slide-up animation-delay-250 border border-green-500/30">
              <span className="text-base sm:text-lg">🎁</span>
              <span>3 Free Credits — No Card Needed</span>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center lg:justify-start sm:justify-center gap-3 sm:gap-4 mb-6 sm:mb-8 animate-slide-up animation-delay-300">
              <Button
                variant="hero"
                size="lg"
                className="w-full sm:w-auto sm:size-xl sm:h-14 sm:px-10 sm:text-lg animate-pulse-glow"
                onClick={() => scrollTo("upload")}
              >
                <Upload className="w-5 h-5" />
                Try Free — Upload a Note
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="w-full sm:w-auto sm:h-14 sm:px-10 sm:text-lg"
                onClick={() => scrollTo("how-it-works")}
              >
                See How It Works
                <ArrowRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap items-center lg:justify-start justify-center gap-x-4 sm:gap-x-5 gap-y-2 text-[11px] sm:text-xs text-muted-foreground mb-8 sm:mb-10 animate-slide-up animation-delay-400">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" /> Secure & Private</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" /> Results in 30s</span>
              <span className="inline-flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" /> No Credit Card</span>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto lg:mx-0 animate-slide-up animation-delay-500">
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-extrabold text-gradient">50K+</div>
                <div className="text-xs text-muted-foreground">Happy Students</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-extrabold text-gradient">1M+</div>
                <div className="text-xs text-muted-foreground">Notes Processed</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-2xl md:text-3xl font-extrabold text-gradient flex items-center lg:justify-start justify-center gap-1">
                  4.9 <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                </div>
                <div className="text-xs text-muted-foreground">2,000+ Reviews</div>
              </div>
            </div>
          </div>

          {/* Right visual mockup */}
          <div className="relative hidden lg:block animate-slide-up animation-delay-300">
            <div className="relative mx-auto max-w-md">
              {/* Glow */}
              <div className="absolute -inset-6 gradient-primary opacity-30 blur-3xl rounded-full" />

              {/* Main card */}
              <div className="relative glass rounded-3xl p-6 shadow-glow border border-border/50 animate-float">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="ml-auto text-xs text-muted-foreground">notes.jpg</span>
                </div>

                <div className="rounded-xl bg-muted/50 p-4 mb-4 border border-border/40">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <FileText className="w-3.5 h-3.5" /> Handwritten input
                  </div>
                  <p className="text-[11px] font-handwriting text-foreground/70 leading-relaxed italic">
                    Newton's 2nd Law: F = ma. The force on an object equals mass times acceleration. Direction of F is same as a…
                  </p>
                </div>

                <div className="flex items-center justify-center my-3">
                  <div className="px-3 py-1 rounded-full gradient-primary text-primary-foreground text-xs font-semibold inline-flex items-center gap-1.5 shadow-soft">
                    <Brain className="w-3.5 h-3.5" /> AI Processing…
                  </div>
                </div>

                <div className="rounded-xl gradient-card p-4 border border-primary/20 space-y-3">
                  <div className="flex items-center gap-2 text-xs text-primary font-semibold">
                    <Sparkles className="w-3.5 h-3.5" /> Flashcard Ready
                  </div>
                  <div className="rounded-lg bg-background/60 p-3 border border-primary/10">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Question</p>
                    <p className="text-xs font-semibold text-foreground mb-2">What does Newton's Second Law state?</p>
                    <p className="text-[10px] uppercase tracking-wider text-accent font-semibold mb-1">Answer</p>
                    <p className="text-xs text-foreground/80">F = ma — Force equals mass × acceleration.</p>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                    <span>Quiz · 5 questions</span>
                    <span className="text-green-600 font-semibold">✓ Ready to study</span>
                  </div>
                </div>
              </div>

              {/* Floating chips */}
              <div className="absolute -top-4 -left-6 glass rounded-2xl px-3 py-2 shadow-card border border-border/50 animate-float">
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <div className="w-7 h-7 rounded-lg gradient-accent flex items-center justify-center">
                    <BookOpen className="w-3.5 h-3.5 text-accent-foreground" />
                  </div>
                  Flashcards
                </div>
              </div>
              <div className="absolute -bottom-4 -right-4 glass rounded-2xl px-3 py-2 shadow-card border border-border/50 animate-float animation-delay-300">
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center">
                    <Zap className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                  Quiz Generated
                </div>
              </div>
              <div className="absolute top-1/3 -right-8 glass rounded-2xl px-3 py-2 shadow-card border border-border/50 animate-float animation-delay-500">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-green-600">
                  <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  +30 XP
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
